// @swiftware/gamma-sdk — easy, typed integration for HTML5 games.
//
//   import { gamma } from '@swiftware/gamma-sdk';
//   await gamma.ready();
//   if (await gamma.spendCoins(50, 'extra_life')) { /* grant reward */ }
//
// Inside the Gamma Games app it talks to the injected host bridge
// (window.GammaSDK). Outside the app the integration is inert by default
// (coin calls return `no_transport`); enable `configure({ mock: true })`
// for local dev testing. `gamma.storage` always falls back to localStorage.

import { createStorage, type GammaStorage } from './storage';
import { call, resolveHost } from './transport';
import type {
  ChallengeCompleteResult,
  ChallengeContext,
  ChallengeResult,
  CoinValue,
  EarnResult,
  GammaConfig,
  HostSdk,
  Player,
  ProgressData,
  SpendResult,
} from './types';

export type {
  ChallengeCompleteResult,
  ChallengeContext,
  ChallengeResult,
  ChallengeType,
  CoinValue,
  EarnResult,
  GammaConfig,
  GammaErrorCode,
  Player,
  ProgressData,
  SpendResult,
} from './types';
export type { GammaStorage } from './storage';

const DEFAULT_COIN_VALUE: CoinValue = {
  usdPerCoin: 0.01,
  currencyCode: 'USD',
  displayName: 'Gamma Coins',
  symbol: 'GC',
  iconUrl: null,
};

class Gamma {
  private config: GammaConfig = { storageKey: 'gamma_save' };
  private hostPromise: Promise<{ host: HostSdk; isInApp: boolean }> | null = null;
  private coinValueCache: CoinValue | null = null;

  /** True once {@link ready} has resolved against the real injected host. */
  isInApp = false;

  /** Built-in progress adapter (account-bound when in-app, else localStorage). */
  readonly storage: GammaStorage = createStorage({
    ensureReady: () => this.ensureHost().then((r) => r.isInApp),
    loadProgress: () => this.loadProgress(),
    saveProgress: (data) => this.saveProgress(data),
    // Read lazily so `configure({ storageKey })` after construction is honoured.
    getStorageKey: () => this.config.storageKey ?? 'gamma_save',
  });

  /**
   * Override defaults BEFORE the first call. Useful to seed the dev mock or
   * change the ready timeout / storage key.
   */
  configure(config: GammaConfig): void {
    this.config = { ...this.config, ...config };
  }

  /**
   * Resolves once the SDK has settled — the real host injected by the app, or
   * (outside the app) the inert/mock fallback after `readyTimeoutMs`. Check
   * {@link isInApp} afterwards to know which. Idempotent.
   */
  async ready(): Promise<void> {
    await this.ensureHost();
  }

  private ensureHost(): Promise<{ host: HostSdk; isInApp: boolean }> {
    if (!this.hostPromise) {
      this.hostPromise = resolveHost(this.config).then((r) => {
        this.isInApp = r.isInApp;
        return r;
      });
    }
    return this.hostPromise;
  }

  // ---- Coins ----

  /** Current coin balance (server-authoritative in-app, mock otherwise). */
  async getCoins(): Promise<number> {
    const { host } = await this.ensureHost();
    const r = await call(host, 'getCoins', []);
    return r.success ? Number(r.balance ?? 0) : 0;
  }

  /** Spend coins. Only grant the reward when the returned `ok` is true. */
  async spendCoins(amount: number, reason: string): Promise<SpendResult> {
    const { host } = await this.ensureHost();
    const r = await call(host, 'spendCoins', [amount, reason]);
    return {
      ok: r.success,
      newBalance: r.newBalance as number | undefined,
      spent: r.spent as number | undefined,
      balance: r.balance as number | undefined,
      error: r.error,
    };
  }

  /** Award coins for an in-game accomplishment (server rate-limited). */
  async earnCoins(amount: number, reason: string): Promise<EarnResult> {
    const { host } = await this.ensureHost();
    const r = await call(host, 'earnCoins', [amount, reason]);
    return {
      ok: r.success,
      newBalance: r.newBalance as number | undefined,
      earned: r.earned as number | undefined,
      error: r.error,
    };
  }

  // ---- Coin value ----

  /**
   * The canonical Gamma Coin value + display metadata, shared by every
   * integrated game. Cached after first read; falls back to sane defaults
   * (incl. on hosts older than 1.1.0 that lack `getCoinValue`).
   */
  async getCoinValue(): Promise<CoinValue> {
    if (this.coinValueCache) return this.coinValueCache;
    const { host } = await this.ensureHost();
    const r = await call(host, 'getCoinValue', []);
    // Only cache a real, successful read. On a transient failure (network
    // blip, no_transport, host < 1.1.0) return defaults WITHOUT caching so a
    // later call retries instead of being stuck on defaults for the session.
    if (!r.success) {
      return { ...DEFAULT_COIN_VALUE };
    }
    const raw = (r.coinValue as Partial<CoinValue>) ?? {};
    this.coinValueCache = { ...DEFAULT_COIN_VALUE, ...raw };
    return this.coinValueCache;
  }

  /** Convert a coin amount to its real-world value using {@link getCoinValue}. */
  async coinsToUsd(amount: number): Promise<number> {
    const { usdPerCoin } = await this.getCoinValue();
    return amount * usdPerCoin;
  }

  /** Format a coin amount for display, e.g. `formatCoins(50)` → "50 GC". */
  async formatCoins(amount: number): Promise<string> {
    const { symbol } = await this.getCoinValue();
    return `${amount} ${symbol}`;
  }

  // ---- Player ----

  /** Non-sensitive public profile, or null if unavailable. */
  async getPlayer(): Promise<Player | null> {
    const { host } = await this.ensureHost();
    const r = await call(host, 'getPlayer', []);
    return r.success ? (r.player as Player) : null;
  }

  // ---- Raw progress (most games should use `gamma.storage`) ----

  /** Low-level account-bound load. Prefer {@link storage}. */
  async loadProgress(): Promise<ProgressData | null> {
    const { host } = await this.ensureHost();
    const r = await call(host, 'loadProgress', []);
    return r.success ? ((r.data as ProgressData | null) ?? null) : null;
  }

  /** Low-level account-bound save. Prefer {@link storage}. */
  async saveProgress(data: ProgressData): Promise<boolean> {
    const { host } = await this.ensureHost();
    const r = await call(host, 'saveProgress', [data]);
    return r.success;
  }

  // ---- Friend challenges (host >= 1.2.0) ----

  /**
   * Friend-challenge integration. When the game is launched in challenge
   * mode the app injects the challenge context; branch once at startup:
   *
   *   const challenge = await gamma.challenges.getActive();
   *   if (challenge) {
   *     // competitive run — report progress + the final result
   *     gamma.challenges.submitScore(score);          // during play (throttled)
   *     await gamma.challenges.complete({ score });   // once, at game over
   *   }
   */
  readonly challenges = {
    /** The active challenge context, or null for a normal play session. */
    getActive: async (): Promise<ChallengeContext | null> => {
      const { host } = await this.ensureHost();
      const r = await call(host, 'getChallenge', []);
      return r.success ? ((r.challenge as ChallengeContext | null) ?? null) : null;
    },

    /**
     * Report the current progressive value (score / elapsed ms / streak)
     * while playing. Powers the live opponent overlay, and for higher-wins
     * types the disconnect fallback (fastest_time requires complete() — a
     * partial elapsed time never counts). Safe to call often — the host
     * throttles. Fire-and-forget.
     */
    submitScore: async (value: number): Promise<{ ok: boolean; error?: string }> => {
      const { host } = await this.ensureHost();
      const r = await call(host, 'submitChallengeScore', [value]);
      return { ok: r.success, error: r.error };
    },

    /**
     * Submit the FINAL result exactly once, at game over. Set the key
     * matching the challenge type: `{score}` for high_score, `{timeMs}` for
     * fastest_time, `{streak}` for level_streak. The app settles the
     * challenge server-side once both players have finished.
     */
    complete: async (result: ChallengeResult): Promise<ChallengeCompleteResult> => {
      const { host } = await this.ensureHost();
      const r = await call(host, 'completeChallenge', [result]);
      return {
        ok: r.success,
        settled: r.settled as boolean | undefined,
        outcome: r.outcome as ChallengeCompleteResult['outcome'],
        potCoins: r.potCoins as number | undefined,
        error: r.error,
      };
    },
  };
}

/** The singleton SDK instance. Import this in your game. */
export const gamma = new Gamma();
export default gamma;
